import { Checkbox, Typography } from '@mui/material';
import { CurrencyCode } from 'further-types/lp/vehicle';
import { Row } from 'further-ui/components/Layout';
import Table, { Column as TableColumn } from 'further-ui/components/Table';
import {
  CalendarDay,
  numberToCurrencyString,
  numberToPercentageString,
} from 'further-ui/utils';
import api from 'lib/trpcClient';
import { sumBy } from 'lodash';
import { Allocation } from '../AllocationsManager';

type Props = {
  vehicleId: string;
  allocations: Array<Allocation>;
  apportionmentPeriods?: Array<{
    periodStartDate: CalendarDay;
    allocations: Array<Allocation>;
  }>;
  baseCurrency: CurrencyCode;
  currency: CurrencyCode;
  allocatedActionLabel: string;
  currentActionIds?: Array<string>;
  date: CalendarDay;
};

type PeriodKey = `period_${number}`;

type RowType = Allocation &
  Record<PeriodKey, number> & {
    id: string;
    investorName: string;
    totalCommitmentLcy: number;
    unallocatedCommitmentLcy: number;
    closeDate?: CalendarDay;
  };

const AllocationsPreview: React.FC<Props> = ({
  vehicleId,
  allocations,
  baseCurrency,
  currency,
  allocatedActionLabel,
  apportionmentPeriods,
  currentActionIds,
  date,
}) => {
  const [commitments] = api.allocations.getCommitments.useSuspenseQuery({
    vehicleId,
    actionIdsToExclude: currentActionIds ?? [],
    date,
  });

  const rows: Array<RowType> = allocations.map((allocation) => {
    const apportionmentAllocations = apportionmentPeriods
      ? Object.fromEntries(
          apportionmentPeriods.map((period, periodIndex) => {
            return [
              `period_${periodIndex}`,
              period.allocations.find(
                (periodAllocation) =>
                  allocation.commitmentId === periodAllocation.commitmentId,
              )?.allocationAmountTcy ?? 0,
            ];
          }),
        )
      : {};

    const commitment = commitments.find(
      (commitment) => commitment.commitmentId === allocation.commitmentId,
    );

    return {
      id: allocation.commitmentId,
      ...allocation,
      ...apportionmentAllocations,
      investorName: commitment?.investorName ?? '',
      totalCommitmentLcy: commitment?.totalCommitmentLcy ?? 0,
      unallocatedCommitmentLcy: commitment?.unallocatedCommitmentLcy ?? 0,
      closeDate: commitment?.closeDate,
    };
  });

  const commitmentColumns: Array<TableColumn<RowType>> = [
    {
      label: 'Investor',
      key: 'investorLabel',
      render: (row) => (
        <Row centered spacing="sm">
          <Checkbox disabled checked={!row.excludeFromAllocation} />
          <Typography variant="h6">{row.investorName}</Typography>
        </Row>
      ),
      footer: 'Total',
    },
    {
      label: 'Total commitment',
      key: 'totalCommitmentLcy',
      render: (row) =>
        numberToCurrencyString(row.totalCommitmentLcy, {
          currency: baseCurrency,
        }),
    },
    {
      label: 'Unallocated commitment',
      key: 'unallocatedCommitmentLcy',
      render: (row) =>
        numberToCurrencyString(row.unallocatedCommitmentLcy, {
          currency: baseCurrency,
        }),
    },

    {
      label: 'Close date',
      key: 'closeDate',
      render: (row) => row.closeDate?.toReadable() ?? '-',
      withVerticalBorder: !!apportionmentPeriods?.length,
    },
  ];

  const apportionmentColumns: Array<TableColumn<RowType>> = apportionmentPeriods
    ? apportionmentPeriods.map((period, periodIndex) => ({
        label: `${allocatedActionLabel} allocated (${period.periodStartDate.toReadable()})`,
        key: `period_${periodIndex}`,
        render: (row) =>
          numberToCurrencyString(row[`period_${periodIndex}`] ?? 0, {
            currency,
          }),
        footer: numberToCurrencyString(
          sumBy(rows, (row) => row[`period_${periodIndex}`] ?? 0),
          {
            currency,
          },
        ),
      }))
    : [];

  const singelModeColumns: Array<TableColumn<(typeof rows)[number]>> = [
    {
      label: `${allocatedActionLabel} allocated`,
      key: 'allocationAmountTcy',
      render: (row) =>
        numberToCurrencyString(row.allocationAmountTcy, {
          currency,
        }),
      footer: numberToCurrencyString(
        sumBy(rows, (row) => row.allocationAmountTcy),
        {
          currency,
        },
      ),
    },
    {
      label: `${allocatedActionLabel} allocated (%)`,
      key: 'allocationPercentage',
      render: (row) => numberToPercentageString(row.allocationPercentage / 100),
      footer: numberToPercentageString(
        sumBy(rows, (row) => row.allocationPercentage) / 100,
      ),
    },
  ];

  const columns: Array<TableColumn<RowType>> = [
    ...commitmentColumns,
    ...(apportionmentPeriods ? apportionmentColumns : singelModeColumns),
  ];

  return <Table columns={columns} rows={rows} />;
};

export default AllocationsPreview;
